import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const LABELS = {
  name: "Name", phone: "Phone", gstin: "GSTIN", fssai: "FSSAI", address: "Address",
  payment_terms_days: "Payment terms (days)", default_department_id: "Default department", aliases: "Other names",
  stock_item_id: "Stock item", supplier_id: "Supplier", category: "Category", unit: "Unit", reorder_level: "Reorder level",
  amount: "Amount", note: "Note", reason: "Reason", type: "Type", lines: "Lines",
};
const label = key => LABELS[key] || key.replaceAll("_", " ").replace(/^./, c => c.toUpperCase());
const show = value => {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.length ? value.map(v => (typeof v === "object" ? JSON.stringify(v) : String(v))).join(", ") : "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const ROW = "grid grid-cols-[1.5rem_minmax(0,1fr)] items-center gap-x-3 gap-y-2 p-3 md:grid-cols-[2.5rem_minmax(0,2fr)_3.5rem_minmax(0,1fr)_minmax(0,2fr)] md:p-2";

/** New stock items with their aliases; unticking a row leaves that item out of the approval. */
export function StockItemsReview({ draft, setDraft, original, readOnly }) {
  const kept = new Set((draft.items || []).map(i => i.ref ?? i.name));
  const toggle = (item, on) => {
    const target = item.ref ?? item.name;
    const items = original.items
      .filter(o => ((o.ref ?? o.name) === target ? on : kept.has(o.ref ?? o.name)))
      .map(o => (draft.items || []).find(d => (d.ref ?? d.name) === (o.ref ?? o.name)) || o);
    setDraft({ ...draft, items });
  };
  const setItem = (key, field, value) =>
    setDraft({ ...draft, items: draft.items.map(i => ((i.ref ?? i.name) === key ? { ...i, [field]: value } : i)) });

  return (
    <div className="rounded-md border" data-testid="stock-items-review">
      {draft.note && <p className="p-3 text-meta text-muted-foreground">Note from the assistant: {draft.note}</p>}
      {/* A row is a grid, not a table row: on a phone the fields stack under the name so nothing is clipped. */}
      <div className={`${ROW} hidden border-b bg-muted/40 text-micro font-medium text-muted-foreground md:grid`}>
        <span>Add</span><span>Name</span><span>Unit</span><span>Category</span><span>Other names (aliases)</span>
      </div>
      {(original.items || []).map((item, i) => {
        const key = item.ref ?? item.name;
        const on = kept.has(key);
        const row = (draft.items || []).find(d => (d.ref ?? d.name) === key) || item;
        return (
          <div key={key} className={`${ROW} border-b last:border-b-0 ${on ? "" : "opacity-50"}`} data-testid={`stock-item-row-${i}`}>
            <Checkbox className="row-span-1 h-5 w-5" checked={on} disabled={readOnly} onCheckedChange={v => toggle(item, Boolean(v))} aria-label={`Include ${item.name}`} data-testid={`stock-item-include-${i}`} />
            <Input className="h-9 min-w-0" disabled={readOnly || !on} value={row.name || ""} onChange={e => setItem(key, "name", e.target.value)} aria-label="Name" data-testid={`stock-item-name-${i}`} />
            <span className="col-start-2 text-meta text-muted-foreground md:col-start-auto md:text-body md:text-foreground">{item.unit}</span>
            <Input className="col-start-2 h-9 min-w-0 md:col-start-auto" disabled={readOnly || !on} value={row.category || ""} onChange={e => setItem(key, "category", e.target.value)} aria-label="Category" placeholder="Category" />
            <Input className="col-start-2 h-9 min-w-0 md:col-start-auto" disabled={readOnly || !on} value={(row.aliases || []).join(", ")} onChange={e => setItem(key, "aliases", e.target.value.split(",").map(a => a.trim()).filter(Boolean))} aria-label="Other names" placeholder="Other names, comma separated" data-testid={`stock-item-aliases-${i}`} />
          </div>
        );
      })}
    </div>
  );
}

/** create_supplier shows the new record; update_supplier shows old → new for what changes. */
export function SupplierReview({ type, draft, setDraft, context, readOnly }) {
  if (type === "update_supplier") {
    const current = context?.current || {};
    const changed = Object.keys(draft).filter(k => k !== "supplier_id" && JSON.stringify(draft[k]) !== JSON.stringify(current[k]));
    return (
      <div className="rounded-md border" data-testid="supplier-diff">
        <p className="p-3 text-body font-semibold">{current.name || `Supplier #${draft.supplier_id}`}</p>
        <Table>
          <TableHeader><TableRow><TableHead>Field</TableHead><TableHead>Now</TableHead><TableHead>After approval</TableHead></TableRow></TableHeader>
          <TableBody>
            {changed.length === 0 && <TableRow><TableCell colSpan={3} className="text-muted-foreground">Nothing differs from the supplier as it is now.</TableCell></TableRow>}
            {changed.map(k => (
              <TableRow key={k} data-testid={`diff-${k}`}>
                <TableCell className="font-medium">{label(k)}</TableCell>
                <TableCell className="text-muted-foreground line-through decoration-danger/50">{show(current[k])}</TableCell>
                <TableCell className="text-success font-medium">{show(draft[k])}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }
  const fields = ["name", "phone", "gstin", "fssai", "address", "payment_terms_days"];
  return (
    <div className="rounded-md border p-3 grid gap-2 sm:grid-cols-2" data-testid="supplier-new">
      <Badge variant="secondary" className="w-fit sm:col-span-2">New supplier</Badge>
      {fields.map(k => (
        <label key={k} className="flex flex-col gap-1">
          <span className="text-micro font-medium text-muted-foreground">{label(k)}</span>
          <Input className="h-9" disabled={readOnly} value={draft[k] ?? ""} onChange={e => setDraft({ ...draft, [k]: e.target.value })} data-testid={`supplier-field-${k}`} />
        </label>
      ))}
      <p className="text-meta text-muted-foreground sm:col-span-2">Other names: {show(draft.aliases)}</p>
    </div>
  );
}

/** Everything else: a readable list of what will be done, never raw JSON on its own. */
export function PayloadView({ payload, context }) {
  const rows = Object.entries(payload || {});
  const itemName = context?.item_names?.[String(payload?.stock_item_id)]?.name;
  return (
    <div className="rounded-md border" data-testid="payload-view">
      <Table>
        <TableBody>
          {rows.map(([k, v]) => (
            <TableRow key={k}>
              <TableCell className="font-medium w-48 align-top">{label(k)}</TableCell>
              <TableCell className="whitespace-pre-wrap break-words">
                {k === "stock_item_id" && itemName ? `${itemName} (#${v})` : show(v)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
